import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import AuthTemplate from 'src/components/atoms/authTemplate'
import Button from 'src/components/atoms/button/button'
import Spin from 'src/components/atoms/spin'
import useLogin from 'src/hooks/useLogin'
import useSessionStorageListener from 'src/hooks/useSessionStorageListener'
import { LoginForm } from 'src/routes/login'
import { routes } from 'src/routes/routes'
import TenantSelector from 'src/routes/tenantSelector/components/tenantSelector'
import redirectToExternalUrl from 'src/shared/helpers/redirectToExternalUrl'
import { getFromStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { useUser } from 'src/state/userState'

import MFAForm from '../MFAForm'
import DieterenSVG from './dieteren_logo.svg'

import Icon from '@ant-design/icons'
import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { Divider } from 'antd'

const logo = () => <img src={DieterenSVG} />

export default function Login() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [searchParams] = useSearchParams()
    const { instance: msalInstance, inProgress } = useMsal()

    const { authenticated, businessUnitBrand } = useUser()

    const { loading, errors, onLogin, onSSOLogin, onMFALogin, onMultiTenantLogin, onValidateRedirect } = useLogin()
    const { uri, secretKey, authResultType, tenants } = useSessionStorageListener('user') || {}

    const [showLoginForm, setShowLoginForm] = useState(false)

    const skipLoginPage = authenticated && businessUnitBrand?.id
    const handleRedirect = async (redirectUrl: string) => {
        try {
            const result = await onValidateRedirect({ redirectUrl })
            if (result.valid) {
                const { accessToken } = getFromStorage('user')
                const params = new URLSearchParams(searchParams)
                params.delete('redirectUrl')
                params.set('token', accessToken)
                const url = new URL(redirectUrl)
                url.search = params.toString()
                redirectToExternalUrl(url.toString())
            } else {
                navigate(routes.HOME)
            }
        } catch {
            navigate(routes.HOME)
        }
    }

    useEffect(() => {
        if (!skipLoginPage) return
        // When the redirect param is set but the user is already logged in.
        const redirectUrl = searchParams.get('redirectUrl')

        if (!redirectUrl) {
            navigate(routes.HOME)
            return
        }

        handleRedirect(redirectUrl)
    }, [skipLoginPage])

    // Prevent flickering UI
    if (skipLoginPage) {
        return null
    }

    const initializeSignIn = () =>
        msalInstance.loginPopup().then((loginResponse) => onSSOLogin(loginResponse, searchParams))

    const showMFA = !!authResultType
    const showTenantSelector = tenants?.length > 0
    const showLoader = inProgress !== InteractionStatus.None || (authenticated && !showTenantSelector)

    if (errors.validateRedirectError) {
        return (
            <AuthTemplate>
                <div>Invalid redirect</div>
            </AuthTemplate>
        )
    }

    return (
        <AuthTemplate>
            {showLoader ? (
                <Spin data-testid="sso-login-loading-screen" />
            ) : showMFA ? (
                <MFAForm
                    uri={uri}
                    secretKey={secretKey}
                    authResultType={authResultType}
                    error={errors.loginMFAUserError}
                    onSubmit={onMFALogin}
                />
            ) : showTenantSelector ? (
                <TenantSelector tenants={tenants} onSubmit={onMultiTenantLogin} />
            ) : (
                <>
                    {showLoginForm ? (
                        <LoginForm
                            submitting={loading}
                            error={errors.loginCredentialUserError || errors.loginSSOUserError}
                            onSubmit={onLogin}
                            onBack={() => setShowLoginForm(false)}
                        />
                    ) : loading ? (
                        <Spin data-testid="sso-logging-in" />
                    ) : (
                        <>
                            <Button type="primary" className="w-full" onClick={() => setShowLoginForm(true)}>
                                {t('Log in with Autralis credentials')}
                            </Button>
                            <Divider>{t('OR')}</Divider>
                            <Button type="primary" className="flex w-full justify-center" onClick={initializeSignIn}>
                                <Icon component={logo} />
                                {t("Sign in with D'Ieteren credentials")}
                            </Button>
                        </>
                    )}
                </>
            )}
        </AuthTemplate>
    )
}
